import React, { useCallback, useEffect, useState } from 'react'
import {
  StyledCol,
  StyledColContainer,
  StyledContainer,
  StyledContinueButtonContainer,
  StyledHeader,
  StyledRowContainer,
} from './ValueRentalAndUsageForm.styled'
import Input from 'components/Input/Input'
import { useAppDispatch } from 'store/hooks'
import { useSelector } from 'react-redux'
import { removeFormField, selectAddressFormData, setFormField } from 'slices/addressFormSlice'
import { useTranslation } from 'react-i18next'
import Button from 'components/Button/Button'
import MultiSelect from 'components/MultiSelect/MultiSelect'
import {
  BuildingType,
  ObjectType,
  OwnershipType,
  RentReferenceType,
  RentalCurrentUsageType,
} from 'types/ApiResponse'
import RadioGroupButtons from 'components/RadioGroupButtons/RadioGroupButtons'
import {
  handleNumberInput,
  suitableForOwnUseSelect,
} from 'components/Modal/ProductFormModal/helper'
import { StyledInputAdornment } from 'components/Modal/ProductFormModal/ProductFormModal.styled'

const ValueRentalAndUsageForm = (props: any) => {
  const { setCurrentSubTab, setCurrentTab } = props
  const dispatch = useAppDispatch()
  const addressFormData = useSelector(selectAddressFormData)
  const { t } = useTranslation()
  const [showValidation, setShowValidation] = useState(false)
  const [validationErrors, setValidationErrors] = useState([])
  const objectType = addressFormData.addressData?.propertyDetails?.objectType
  const buildingType = addressFormData.addressData?.propertyDetails?.buildingType
  const numberRegex = /^\d*(,\d{0,2})?$/

  const getRentalCurrentUsageOptions = () => {
    const options = [
      {
        label: `rentalCurrentUsage.${RentalCurrentUsageType.OWNER_OCCUPIED.toLowerCase()}`,
        value: RentalCurrentUsageType.OWNER_OCCUPIED,
      },
      {
        label: `rentalCurrentUsage.${RentalCurrentUsageType.PARTIALLY_OWNER_OCCUPIED.toLowerCase()}`,
        value: RentalCurrentUsageType.PARTIALLY_OWNER_OCCUPIED,
      },
      {
        label: `rentalCurrentUsage.${RentalCurrentUsageType.RENTED.toLowerCase()}`,
        value: RentalCurrentUsageType.RENTED,
      },
    ]

    const filteredOptions = options.filter(
      (item) => item.value !== RentalCurrentUsageType.PARTIALLY_OWNER_OCCUPIED
    )

    switch (objectType) {
      case ObjectType.HOUSE:
        if (buildingType === BuildingType.SINGLE_FAMILY_HOUSE) return filteredOptions
        else return options
      case ObjectType.APARTMENT:
        return filteredOptions
    }

    return []
  }

  const rentalReferenceOptions = [
    {
      label: `rentalReference.${RentReferenceType.EURO_PER_SQUARE_METER.toLowerCase()}`,
      value: RentReferenceType.EURO_PER_SQUARE_METER,
    },
    {
      label: `rentalReference.${RentReferenceType.EURO_PER_YEAR.toLowerCase()}`,
      value: RentReferenceType.EURO_PER_YEAR,
    },
  ]

  const validateNumericField = useCallback(
    (currentValue, min, max, key, isFloat = false) => {
      const numericValue = isFloat
        ? parseFloat(currentValue.replace(',', '.'))
        : parseInt(currentValue)
      const numericMin = parseInt(min)
      const numericMax = parseInt(max)
      if (numericValue >= numericMax) {
        dispatch(setFormField({ key, value: `${numericMax}` }))
      } else if (numericValue <= numericMin) {
        dispatch(setFormField({ key, value: `${numericMin}` }))
      }
    },
    [dispatch]
  )

  const validate = (name, property) => {
    if (property === null || property === undefined || property === '') {
      if (!validationErrors.includes(name)) {
        setValidationErrors((errors) => [name, ...errors])
      }
      return showValidation && true
    } else {
      if (validationErrors.includes(name)) {
        setValidationErrors((errors) => errors.filter((error) => error !== name))
      }
      return false
    }
  }

  const validateRent = (name, property) => {
    const area =
      name === 'actualRent'
        ? addressFormData.addressData?.propertyDetails?.livingArea
        : addressFormData.addressData?.propertyDetails?.commercialArea
    const referenceUnit =
      name === 'actualRent'
        ? addressFormData.addressData?.propertyDetails?.rentalInformation?.actualRentReference
        : addressFormData.addressData?.propertyDetails?.rentalInformation
            ?.actualCommercialRentReference
    let price = 0
    if (area && referenceUnit && property && parseFloat(property) > 0) {
      if (referenceUnit === RentReferenceType.EURO_PER_YEAR) {
        price = parseFloat(property) / 12 / parseFloat(area)
      }
      if (referenceUnit === RentReferenceType.EURO_PER_SQUARE_METER) {
        price = parseFloat(property)
      }
      if (price < 2) {
        if (!validationErrors.includes(name)) {
          setValidationErrors((errors) => [name, ...errors])
        }
        return showValidation && true
      }
    }
    return validate(name, price)
  }

  const validateCoownership = (name) => {
    if (name === 'numerator') {
      if (
        !addressFormData.addressData?.propertyDetails?.coownershipNumerator &&
        addressFormData.addressData?.propertyDetails?.coownershipDenominator
      ) {
        if (!validationErrors.includes('coownershipNumerator')) {
          setValidationErrors((errors) => ['coownershipNumerator', ...errors])
        }
        return showValidation && true
      } else if (
        addressFormData.addressData?.propertyDetails?.coownershipNumerator &&
        addressFormData.addressData?.propertyDetails?.coownershipDenominator &&
        parseFloat(
          addressFormData.addressData?.propertyDetails?.coownershipNumerator.replace(',', '.')
        ) >
          parseFloat(
            addressFormData.addressData?.propertyDetails?.coownershipDenominator.replace(',', '.')
          )
      ) {
        if (!validationErrors.includes('coownershipNumerator')) {
          setValidationErrors((errors) => ['coownershipNumerator', ...errors])
        }
        return showValidation && true
      } else {
        if (
          objectType === ObjectType.HOUSE &&
          (addressFormData.addressData?.propertyDetails?.ownership === OwnershipType.SHARED_LEASE ||
            addressFormData.addressData?.propertyDetails?.ownership === OwnershipType.SHARED)
        ) {
          return validate(
            'coownershipNumerator',
            addressFormData.addressData?.propertyDetails?.coownershipNumerator
          )
        } else if (validationErrors.includes('coownershipNumerator')) {
          setValidationErrors((errors) =>
            errors.filter((error) => error !== 'coownershipNumerator')
          )
        }
      }
    }
    if (name === 'denominator') {
      if (
        !addressFormData.addressData?.propertyDetails?.coownershipDenominator &&
        addressFormData.addressData?.propertyDetails?.coownershipNumerator
      ) {
        if (!validationErrors.includes('coownershipDenominator')) {
          setValidationErrors((errors) => ['coownershipDenominator', ...errors])
        }
        return showValidation && true
      } else if (
        addressFormData.addressData?.propertyDetails?.coownershipNumerator &&
        addressFormData.addressData?.propertyDetails?.coownershipDenominator &&
        parseFloat(
          addressFormData.addressData?.propertyDetails?.coownershipNumerator.replace(',', '.')
        ) >
          parseFloat(
            addressFormData.addressData?.propertyDetails?.coownershipDenominator.replace(',', '.')
          )
      ) {
        if (!validationErrors.includes('coownershipDenominator')) {
          setValidationErrors((errors) => ['coownershipDenominator', ...errors])
        }
        return showValidation && true
      } else {
        if (
          objectType === ObjectType.HOUSE &&
          (addressFormData.addressData?.propertyDetails?.ownership === OwnershipType.SHARED_LEASE ||
            addressFormData.addressData?.propertyDetails?.ownership === OwnershipType.SHARED)
        ) {
          return validate(
            'coownershipDenominator',
            addressFormData.addressData?.propertyDetails?.coownershipDenominator
          )
        } else if (validationErrors.includes('coownershipDenominator')) {
          setValidationErrors((errors) =>
            errors.filter((error) => error !== 'coownershipDenominator')
          )
        }
      }
    }
    return false
  }

  const validationMultiSelect = (value, options, formFieldName) => {
    if (!options) {
      return value
    }
    const matchingOption = options.find((option) => option.value === value || option.key === value)
    if ((value && !matchingOption) || !value) {
      dispatch(removeFormField(formFieldName))
      return false
    }
    return true
  }

  const onChangeSuitableForOwnUse = useCallback(
    (suitableForOwnUse?: boolean) => {
      if (suitableForOwnUse !== null) {
        dispatch(
          setFormField({ key: 'propertyDetails.suitableForOwnUse', value: suitableForOwnUse })
        )
        if (`${suitableForOwnUse}` === `true`) {
          dispatch(removeFormField('propertyDetails.rentalInformation.actualRent'))
          dispatch(removeFormField('propertyDetails.rentalInformation.actualRentReference'))
          dispatch(removeFormField('propertyDetails.coownershipNumerator'))
          dispatch(removeFormField('propertyDetails.coownershipDenominator'))
          dispatch(removeFormField('propertyDetails.plotArea'))
          setValidationErrors([])
        }
      } else {
        dispatch(removeFormField('propertyDetails.suitableForOwnUse'))
      }
    },
    [dispatch, setValidationErrors]
  )

  const onChangeRentalCurrentUsage = useCallback(
    (rentalCurrentUsage: RentalCurrentUsageType) => {
      dispatch(
        setFormField({ key: 'propertyDetails.rentalCurrentUsage', value: rentalCurrentUsage })
      )
      if (objectType === ObjectType.APARTMENT) {
        if (rentalCurrentUsage === RentalCurrentUsageType.OWNER_OCCUPIED) {
          onChangeSuitableForOwnUse(null)
          dispatch(removeFormField('propertyDetails.rentalInformation.actualRent'))
          dispatch(removeFormField('propertyDetails.rentalInformation.actualRentReference'))
          dispatch(removeFormField('propertyDetails.coownershipNumerator'))
          dispatch(removeFormField('propertyDetails.coownershipDenominator'))
          dispatch(removeFormField('propertyDetails.plotArea'))
          setValidationErrors([])
        } else {
          if (
            addressFormData.addressData?.propertyDetails?.suitableForOwnUse === null ||
            addressFormData.addressData?.propertyDetails?.suitableForOwnUse === undefined
          ) {
            onChangeSuitableForOwnUse(true)
          }
        }
      }
    },
    [
      dispatch,
      onChangeSuitableForOwnUse,
      setValidationErrors,
      objectType,
      addressFormData.addressData,
    ]
  )

  const onChangeActualRent = (actualRent: string, validate: boolean = false) => {
    if (actualRent && actualRent.length === 0) {
      dispatch(removeFormField('propertyDetails.rentalInformation.actualRent'))
      return
    }
    if (numberRegex.test(actualRent)) {
      dispatch(
        setFormField({
          key: 'propertyDetails.rentalInformation.actualRent',
          value: actualRent,
        })
      )
      if (validate) {
        validateNumericField(actualRent, 0, 1000000, 'propertyDetails.rentalInformation.actualRent')
      }
    }
  }

  const onChangeActualCommercialRent = (
    actualCommercialRent: string,
    validate: boolean = false
  ) => {
    if (actualCommercialRent && actualCommercialRent.length === 0) {
      dispatch(removeFormField('propertyDetails.rentalInformation.actualCommercialRent'))
      return
    }
    if (numberRegex.test(actualCommercialRent)) {
      dispatch(
        setFormField({
          key: 'propertyDetails.rentalInformation.actualCommercialRent',
          value: actualCommercialRent,
        })
      )
      if (validate) {
        validateNumericField(
          actualCommercialRent,
          0,
          1000000,
          'propertyDetails.rentalInformation.actualCommercialRent'
        )
      }
    }
  }

  const onChangeActualRentReference = useCallback(
    (actualRentReference: RentReferenceType) => {
      dispatch(
        setFormField({
          key: 'propertyDetails.rentalInformation.actualRentReference',
          value: actualRentReference,
        })
      )
    },
    [dispatch]
  )

  const onChangeActualCommercialRentReference = useCallback(
    (actualCommercialRentReference: RentReferenceType) => {
      dispatch(
        setFormField({
          key: 'propertyDetails.rentalInformation.actualCommercialRentReference',
          value: actualCommercialRentReference,
        })
      )
    },
    [dispatch]
  )

  const onChangeCoownershipNumerator = useCallback(
    (coownershipNumerator: string, validate: boolean = false) => {
      if (coownershipNumerator && coownershipNumerator.length === 0) {
        dispatch(removeFormField('propertyDetails.coownershipNumerator'))
        return
      }
      const regex = /^\d*(,\d{0,3})?$/
      if (regex.test(coownershipNumerator)) {
        dispatch(
          setFormField({
            key: 'propertyDetails.coownershipNumerator',
            value: coownershipNumerator,
          })
        )
        if (validate) {
          validateNumericField(
            coownershipNumerator,
            0,
            1000000,
            'propertyDetails.coownershipNumerator',
            true
          )
        }
      }
    },
    [dispatch, validateNumericField]
  )

  const onChangeCoownershipDenominator = useCallback(
    (coownershipDenominator: string, validate: boolean = false) => {
      if (coownershipDenominator && coownershipDenominator.length === 0) {
        dispatch(removeFormField('propertyDetails.coownershipDenominator'))
        return
      }
      const regex = /^\d*(,\d{0,3})?$/
      if (regex.test(coownershipDenominator)) {
        dispatch(
          setFormField({
            key: 'propertyDetails.coownershipDenominator',
            value: coownershipDenominator,
          })
        )
        if (validate) {
          validateNumericField(
            coownershipDenominator,
            0,
            1000000,
            'propertyDetails.coownershipDenominator',
            true
          )
        }
      }
    },
    [dispatch, validateNumericField]
  )

  const onChangePlotArea = (plotArea: string, validate: boolean = false) => {
    if (plotArea && plotArea.length === 0) {
      dispatch(removeFormField('propertyDetails.plotArea'))
      return
    }
    if (numberRegex.test(plotArea)) {
      dispatch(setFormField({ key: 'propertyDetails.plotArea', value: plotArea }))
      if (validate) {
        validateNumericField(plotArea, 1, 5000, 'propertyDetails.plotArea', true)
      }
    }
  }

  const onBackClick = () => {
    setCurrentSubTab((tab) => tab - 1)
  }

  const onContinueClick = () => {
    setShowValidation(true)
    if (validationErrors.length === 0) {
      setCurrentTab((tab) => tab + 1)
    }
  }

  useEffect(() => {
    if (
      !addressFormData.addressData?.propertyDetails?.rentalInformation?.actualRentReference &&
      objectType !== ObjectType.HOUSE
    ) {
      if (objectType === ObjectType.MULTI_FAMILY_HOUSE) {
        onChangeActualRentReference(RentReferenceType.EURO_PER_SQUARE_METER)
      }
      if (
        objectType === ObjectType.APARTMENT &&
        `${addressFormData.addressData?.propertyDetails?.suitableForOwnUse}` === 'false'
      ) {
        onChangeActualRentReference(RentReferenceType.EURO_PER_SQUARE_METER)
      }
    }
    if (
      !addressFormData.addressData?.propertyDetails?.rentalInformation
        ?.actualCommercialRentReference &&
      objectType === ObjectType.MULTI_FAMILY_HOUSE
    ) {
      onChangeActualCommercialRentReference(RentReferenceType.EURO_PER_SQUARE_METER)
    }
    if (
      !addressFormData.addressData?.propertyDetails?.rentalCurrentUsage &&
      (objectType === ObjectType.HOUSE || objectType === ObjectType.APARTMENT)
    ) {
      onChangeRentalCurrentUsage(RentalCurrentUsageType.OWNER_OCCUPIED)
    }
  }, [
    objectType,
    addressFormData.addressData,
    onChangeActualRentReference,
    onChangeActualCommercialRentReference,
    onChangeSuitableForOwnUse,
    onChangeRentalCurrentUsage,
  ])

  useEffect(() => {
    onChangeRentalCurrentUsage(addressFormData.addressData?.propertyDetails?.rentalCurrentUsage)
    onChangeActualRent(
      addressFormData.addressData?.propertyDetails?.rentalInformation?.actualRent,
      true
    )
    onChangeActualCommercialRent(
      addressFormData.addressData?.propertyDetails?.rentalInformation?.actualCommercialRent,
      true
    )
    onChangeCoownershipNumerator(
      addressFormData.addressData?.propertyDetails?.coownershipNumerator,
      true
    )
    onChangeCoownershipDenominator(
      addressFormData.addressData?.propertyDetails?.coownershipDenominator,
      true
    )
    onChangePlotArea(addressFormData.addressData?.propertyDetails?.plotArea, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <StyledContainer>
        <StyledHeader>{t('newAddressModal.objectDataTab.headers.rentAndUsage')}</StyledHeader>
        <StyledRowContainer>
          {objectType !== ObjectType.MULTI_FAMILY_HOUSE && (
            <StyledColContainer>
              <StyledCol>
                <MultiSelect
                  value={addressFormData.addressData?.propertyDetails?.rentalCurrentUsage || ''}
                  options={getRentalCurrentUsageOptions()}
                  label={`${t('newAddressModal.objectDataTab.rentalCurrentUsage')}`}
                  onChange={(event) => onChangeRentalCurrentUsage(event.target.value)}
                  validation={validationMultiSelect}
                  formFieldName="propertyDetails.rentalCurrentUsage"
                />
              </StyledCol>
            </StyledColContainer>
          )}
          {objectType === ObjectType.APARTMENT && (
            <StyledColContainer
              $disabled={
                addressFormData.addressData?.propertyDetails?.rentalCurrentUsage ===
                  RentalCurrentUsageType.OWNER_OCCUPIED ||
                !addressFormData.addressData?.propertyDetails?.rentalCurrentUsage
              }
            >
              <StyledCol>
                <RadioGroupButtons
                  disabled={
                    addressFormData.addressData?.propertyDetails?.rentalCurrentUsage ===
                      RentalCurrentUsageType.OWNER_OCCUPIED ||
                    !addressFormData.addressData?.propertyDetails?.rentalCurrentUsage
                  }
                  options={suitableForOwnUseSelect}
                  label={`${t('newAddressModal.objectDataTab.suitableForOwnUse')}`}
                  onChange={(event) => onChangeSuitableForOwnUse(event.target.value)}
                  value={`${addressFormData.addressData?.propertyDetails?.suitableForOwnUse}`}
                />
              </StyledCol>
            </StyledColContainer>
          )}
        </StyledRowContainer>
        {objectType !== ObjectType.HOUSE && (
          <>
            <StyledRowContainer>
              <StyledColContainer
                style={{
                  flexDirection: 'row',
                  alignConent: 'center',
                  gap: '18.5px',
                  width: '100%',
                }}
              >
                <StyledCol
                  validationError={validateRent(
                    'actualRent',
                    addressFormData.addressData?.propertyDetails?.rentalInformation?.actualRent
                  )}
                >
                  <Input
                    style={{ minWidth: '230px' }}
                    disabled={
                      objectType === ObjectType.APARTMENT &&
                      `${addressFormData.addressData?.propertyDetails?.suitableForOwnUse}` !==
                        'false'
                    }
                    value={
                      addressFormData.addressData?.propertyDetails?.rentalInformation?.actualRent ||
                      ''
                    }
                    onChange={(event) => onChangeActualRent(event.target.value)}
                    onBlur={(event) => onChangeActualRent(event.target.value, true)}
                    onKeyPress={(event) => handleNumberInput(event, true)}
                    defaultValue={
                      addressFormData.addressData?.propertyDetails?.rentalInformation?.actualRent ||
                      ''
                    }
                    label={`${t('newAddressModal.objectDataTab.rentalInformation.actualRent')}`}
                  />
                </StyledCol>
                <StyledCol>
                  <MultiSelect
                    style={{ minWidth: '230px' }}
                    disabled={
                      objectType === ObjectType.APARTMENT &&
                      `${addressFormData.addressData?.propertyDetails?.suitableForOwnUse}` !==
                        'false'
                    }
                    value={
                      addressFormData.addressData?.propertyDetails?.rentalInformation
                        ?.actualRentReference || ''
                    }
                    options={rentalReferenceOptions}
                    label={`${t(
                      'newAddressModal.objectDataTab.rentalInformation.actualRentReference'
                    )}`}
                    onChange={(event) => onChangeActualRentReference(event.target.value)}
                    validation={validationMultiSelect}
                    formFieldName="propertyDetails.rentalInformation.actualRentReference"
                  />
                </StyledCol>
                <StyledCol style={{ paddingTop: '10px', color: 'red' }}>
                  {showValidation && validationErrors.includes('actualRent') && (
                    <span>{t('newAddressModal.objectDataTab.rentalInformation.rentTooLow')}</span>
                  )}
                </StyledCol>
              </StyledColContainer>
            </StyledRowContainer>
          </>
        )}
        {buildingType === BuildingType.RESIDENTIAL_AND_COMMERCIAL && (
          <StyledRowContainer>
            <StyledColContainer
              style={{
                flexDirection: 'row',
                alignConent: 'center',
                gap: '18.5px',
                width: '100%',
              }}
            >
              <StyledCol
                validationError={validateRent(
                  'actualCommercialRent',
                  addressFormData.addressData?.propertyDetails?.rentalInformation
                    ?.actualCommercialRent
                )}
              >
                <Input
                  style={{ minWidth: '230px' }}
                  value={
                    addressFormData.addressData?.propertyDetails?.rentalInformation
                      ?.actualCommercialRent || ''
                  }
                  onChange={(event) => onChangeActualCommercialRent(event.target.value)}
                  onBlur={(event) => onChangeActualCommercialRent(event.target.value, true)}
                  onKeyPress={(event) => handleNumberInput(event, true)}
                  defaultValue={
                    addressFormData.addressData?.propertyDetails?.rentalInformation
                      ?.actualCommercialRent || ''
                  }
                  label={`${t(
                    'newAddressModal.objectDataTab.rentalInformation.actualCommercialRent'
                  )}`}
                />
              </StyledCol>
              <StyledCol>
                <MultiSelect
                  style={{ minWidth: '230px' }}
                  value={
                    addressFormData.addressData?.propertyDetails?.rentalInformation
                      ?.actualCommercialRentReference || ''
                  }
                  options={rentalReferenceOptions}
                  label={`${t(
                    'newAddressModal.objectDataTab.rentalInformation.actualCommercialRentReference'
                  )}`}
                  onChange={(event) => onChangeActualCommercialRentReference(event.target.value)}
                  validation={validationMultiSelect}
                  formFieldName="propertyDetails.rentalInformation.actualCommercialRentReference"
                />
              </StyledCol>
              <StyledCol style={{ paddingTop: '10px', color: 'red' }}>
                {showValidation && validationErrors.includes('actualCommercialRent') && (
                  <span>{t('newAddressModal.objectDataTab.rentalInformation.rentTooLow')}</span>
                )}
              </StyledCol>
            </StyledColContainer>
          </StyledRowContainer>
        )}
        {objectType !== ObjectType.MULTI_FAMILY_HOUSE &&
          addressFormData.addressData?.propertyDetails?.ownership !== OwnershipType.FULL &&
          addressFormData.addressData?.propertyDetails?.ownership !== OwnershipType.LEASE && (
            <>
              <StyledHeader
                disabled={
                  `${addressFormData.addressData?.propertyDetails?.suitableForOwnUse}` === 'true' ||
                  (objectType === ObjectType.APARTMENT &&
                    addressFormData.addressData?.propertyDetails?.rentalCurrentUsage ===
                      RentalCurrentUsageType.OWNER_OCCUPIED) ||
                  (objectType === ObjectType.APARTMENT &&
                    !addressFormData.addressData?.propertyDetails?.rentalCurrentUsage)
                }
              >
                {t('newAddressModal.objectDataTab.headers.coownership')}
              </StyledHeader>
              <StyledRowContainer>
                {objectType === ObjectType.APARTMENT && (
                  <StyledColContainer>
                    <StyledCol
                      validationError={
                        `${addressFormData.addressData?.propertyDetails?.suitableForOwnUse}` ===
                          'true' ||
                        addressFormData.addressData?.propertyDetails?.suitableForOwnUse ===
                          undefined ||
                        (objectType === ObjectType.APARTMENT &&
                          addressFormData.addressData?.propertyDetails?.rentalCurrentUsage ===
                            RentalCurrentUsageType.OWNER_OCCUPIED)
                          ? null
                          : validate(
                              'plotArea',
                              addressFormData.addressData?.propertyDetails?.plotArea
                            )
                      }
                    >
                      <Input
                        disabled={
                          `${addressFormData.addressData?.propertyDetails?.suitableForOwnUse}` ===
                            'true' ||
                          addressFormData.addressData?.propertyDetails?.suitableForOwnUse ===
                            undefined ||
                          (objectType === ObjectType.APARTMENT &&
                            addressFormData.addressData?.propertyDetails?.rentalCurrentUsage ===
                              RentalCurrentUsageType.OWNER_OCCUPIED) ||
                          (objectType === ObjectType.APARTMENT &&
                            !addressFormData.addressData?.propertyDetails?.rentalCurrentUsage)
                        }
                        value={addressFormData.addressData?.propertyDetails?.plotArea || ''}
                        onChange={(event) => onChangePlotArea(event.target.value)}
                        onBlur={(event) => onChangePlotArea(event.target.value, true)}
                        onKeyPress={(event) => handleNumberInput(event, true)}
                        defaultValue={addressFormData.addressData?.propertyDetails?.plotArea || ''}
                        label={`${t('newAddressModal.objectDataTab.plotArea')}*`}
                        helperText={`max. 5000`}
                        InputProps={{
                          endAdornment: (
                            <StyledInputAdornment
                              position="end"
                              disabled={
                                `${addressFormData.addressData?.propertyDetails?.suitableForOwnUse}` ===
                                  'true' ||
                                addressFormData.addressData?.propertyDetails?.suitableForOwnUse ===
                                  undefined ||
                                (objectType === ObjectType.APARTMENT &&
                                  addressFormData.addressData?.propertyDetails
                                    ?.rentalCurrentUsage ===
                                    RentalCurrentUsageType.OWNER_OCCUPIED) ||
                                (objectType === ObjectType.APARTMENT &&
                                  !addressFormData.addressData?.propertyDetails?.rentalCurrentUsage)
                              }
                            >
                              m2
                            </StyledInputAdornment>
                          ),
                        }}
                      />
                    </StyledCol>
                  </StyledColContainer>
                )}
              </StyledRowContainer>
              <StyledRowContainer>
                <StyledColContainer style={{ flexDirection: 'row', alignConent: 'center' }}>
                  <StyledCol validationError={validateCoownership('numerator')}>
                    <Input
                      disabled={
                        `${addressFormData.addressData?.propertyDetails?.suitableForOwnUse}` ===
                          'true' ||
                        (objectType === ObjectType.APARTMENT &&
                          addressFormData.addressData?.propertyDetails?.rentalCurrentUsage ===
                            RentalCurrentUsageType.OWNER_OCCUPIED) ||
                        (objectType === ObjectType.APARTMENT &&
                          !addressFormData.addressData?.propertyDetails?.rentalCurrentUsage)
                      }
                      value={
                        addressFormData.addressData?.propertyDetails?.coownershipNumerator || ''
                      }
                      onChange={(event) => onChangeCoownershipNumerator(event.target.value)}
                      onBlur={(event) => onChangeCoownershipNumerator(event.target.value, true)}
                      onKeyPress={(event) => handleNumberInput(event, true)}
                      defaultValue={
                        addressFormData.addressData?.propertyDetails?.coownershipNumerator || ''
                      }
                      label={`${t('newAddressModal.objectDataTab.coownershipNumerator')}${
                        objectType === ObjectType.HOUSE &&
                        (addressFormData.addressData?.propertyDetails?.ownership ===
                          OwnershipType.SHARED_LEASE ||
                          addressFormData.addressData?.propertyDetails?.ownership ===
                            OwnershipType.SHARED)
                          ? '*'
                          : ''
                      }`}
                    />
                  </StyledCol>
                  <span
                    style={{
                      fontSize: '30px',
                      paddingLeft: '5px',
                      paddingRight: '5px',
                      color:
                        `${addressFormData.addressData?.propertyDetails?.suitableForOwnUse}` ===
                          'true' ||
                        (objectType === ObjectType.APARTMENT &&
                          addressFormData.addressData?.propertyDetails?.rentalCurrentUsage ===
                            RentalCurrentUsageType.OWNER_OCCUPIED) ||
                        (objectType === ObjectType.APARTMENT &&
                          !addressFormData.addressData?.propertyDetails?.rentalCurrentUsage)
                          ? '#ededed'
                          : '#7ca5ca',
                    }}
                  >
                    /
                  </span>
                  <StyledCol validationError={validateCoownership('denominator')}>
                    <Input
                      disabled={
                        `${addressFormData.addressData?.propertyDetails?.suitableForOwnUse}` ===
                          'true' ||
                        (objectType === ObjectType.APARTMENT &&
                          addressFormData.addressData?.propertyDetails?.rentalCurrentUsage ===
                            RentalCurrentUsageType.OWNER_OCCUPIED) ||
                        (objectType === ObjectType.APARTMENT &&
                          !addressFormData.addressData?.propertyDetails?.rentalCurrentUsage)
                      }
                      value={
                        addressFormData.addressData?.propertyDetails?.coownershipDenominator || ''
                      }
                      onChange={(event) => onChangeCoownershipDenominator(event.target.value)}
                      onBlur={(event) => onChangeCoownershipDenominator(event.target.value, true)}
                      onKeyPress={(event) => handleNumberInput(event, true)}
                      defaultValue={
                        addressFormData.addressData?.propertyDetails?.coownershipDenominator || ''
                      }
                      label={`${t('newAddressModal.objectDataTab.coownershipDenominator')}${
                        objectType === ObjectType.HOUSE &&
                        (addressFormData.addressData?.propertyDetails?.ownership ===
                          OwnershipType.SHARED_LEASE ||
                          addressFormData.addressData?.propertyDetails?.ownership ===
                            OwnershipType.SHARED)
                          ? '*'
                          : ''
                      }`}
                    />
                  </StyledCol>
                </StyledColContainer>
              </StyledRowContainer>
            </>
          )}
      </StyledContainer>
      <StyledContinueButtonContainer>
        <Button kind="primary" onClick={() => onBackClick()}>
          {t('common.backButton')}
        </Button>
        <Button kind="primary" onClick={() => onContinueClick()}>
          {t('common.continueButton')}
        </Button>
      </StyledContinueButtonContainer>
    </>
  )
}

export default ValueRentalAndUsageForm
